/**
 * 运营-对账中心（F6）+ 哈希链校验（合规核心）
 * 两个功能合并一页，因为都是合规视角
 */

import { useState } from 'react'
import { Card, Table, Button, Badge, useApi, money, time } from '../../components/ui'
import { api } from '../../api/client'
import { HelpBubble } from '../../components/HelpBubble'

interface Report {
  report_id: string
  report_date: string
  fund_match: boolean | null
  order_match: boolean | null
  invoice_match: boolean | null
  pool_balance: boolean | null
  tx_count: number
  total_amount: string
  error_count: number
  errors: Array<{ dim: string; desc: string }>
  daily_digest: string | null
  generated_at: string
}
interface Evidence {
  evidence_id: string
  entity_type: string
  entity_id: string
  flow_type: string
  payload_hash: string
  chain_hash: string
  prev_chain_hash: string
  timestamp: string
}

export function AdminAudit() {
  const [tab, setTab] = useState<'recon' | 'chain'>('recon')
  const reports = useApi<Report[]>(async () => api.get('/admin/audit/reports'), [])
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [running, setRunning] = useState(false)
  const [err, setErr] = useState('')

  async function runRecon() {
    setRunning(true)
    setErr('')
    try {
      await api.post('/admin/audit/reconcile', { date })
      reports.refresh()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>对账中心</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Button variant={tab === 'recon' ? 'primary' : 'default'} size="sm" onClick={() => setTab('recon')}>
          对账报告
        </Button>
        <Button variant={tab === 'chain' ? 'primary' : 'default'} size="sm" onClick={() => setTab('chain')}>
          哈希链校验
        </Button>
      </div>

      {tab === 'recon' ? (
        <>
          <Card title="触发对账">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4 }}
              />
              <Button onClick={runRecon} disabled={running}>
                {running ? '对账中...' : '触发对账'}
              </Button>
            </div>
            {err && <div style={{ color: '#dc2626', marginTop: 8 }}>{err}</div>}
            <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
              对账比对：资金流（分账三方之和=订单额）+ 发票流（每分账订单有商品票+服务费票）+ 汇总户平衡
            </div>
          </Card>

          <Card title={`对账报告（${reports.data?.length ?? 0}）`}>
            <Table<Report>
              rows={reports.data ?? []}
              empty="暂无报告"
              columns={[
                { key: 'report_date', title: '日期', render: (r) => r.report_date.slice(0, 10) },
                { key: 'tx_count', title: '交易数' },
                { key: 'total_amount', title: '总额', render: (r) => money(r.total_amount) },
                {
                  key: 'fund_match',
                  title: '资金流',
                  render: (r) => <MatchBadge ok={r.fund_match} />,
                },
                {
                  key: 'invoice_match',
                  title: '发票流',
                  render: (r) => <MatchBadge ok={r.invoice_match} />,
                },
                {
                  key: 'pool_balance',
                  title: '汇总户平衡',
                  render: (r) => <MatchBadge ok={r.pool_balance} />,
                },
                { key: 'error_count', title: '差错数', render: (r) => (r.error_count > 0 ? <span style={{ color: '#dc2626' }}>{r.error_count}</span> : '0') },
              ]}
            />
          </Card>
        </>
      ) : (
        <ChainCheck />
      )}
      <HelpBubble pageKey="admin-audit" title="对账中心">
        <p>对账触发后系统自动比对四流（合同/资金/发票/物流）一致性，发现不一致生成对账错误。</p>
        <p>哈希链校验可验证存证是否被篡改——单条校验查指定记录，全链校验检查连续性。</p>
      </HelpBubble>
    </div>
  )
}

function MatchBadge({ ok }: { ok: boolean | null }) {
  if (ok === null) return <span style={{ color: '#888' }}>-</span>
  return ok ? <Badge status="SUCCESS" label="一致" /> : <Badge status="FAILED" label="不一致" />
}

function ChainCheck() {
  const list = useApi<Evidence[]>(async () => api.get('/admin/chain?limit=50'), [])
  const [verifyId, setVerifyId] = useState('')
  const [verifyResult, setVerifyResult] = useState<{ tampered: boolean; valid: boolean } | null>(null)
  const [rangeResult, setRangeResult] = useState<{ total: number; broken: number; continuous: boolean } | null>(null)
  const [busy, setBusy] = useState(false)

  async function verify() {
    if (!verifyId) return
    setBusy(true)
    try {
      const r = await api.post<{ tampered: boolean; valid: boolean }>('/admin/chain/verify', { evidenceId: verifyId })
      setVerifyResult(r)
    } finally {
      setBusy(false)
    }
  }
  async function verifyRange() {
    setBusy(true)
    try {
      const r = await api.post<{ total: number; broken_links: number; continuous: boolean }>('/admin/chain/verify-range', {
        fromDate: '2020-01-01T00:00:00Z',
        toDate: '2099-12-31T00:00:00Z',
      })
      setRangeResult({ total: r.total, broken: r.broken_links, continuous: r.continuous })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Card title="单条存证校验">
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input
            placeholder="输入 evidence_id"
            value={verifyId}
            onChange={(e) => setVerifyId(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, flex: 1 }}
          />
          <Button onClick={verify} disabled={busy} size="sm">
            校验
          </Button>
        </div>
        {verifyResult && (
          <div style={{ fontSize: 13 }}>
            结果：{verifyResult.valid ? <span style={{ color: '#166534' }}>✓ 未被篡改</span> : <span style={{ color: '#dc2626' }}>✗ 已篡改</span>}
          </div>
        )}
      </Card>

      <Card title="全链连续性校验">
        <Button onClick={verifyRange} disabled={busy} size="sm">
          校验全部存证链连续性
        </Button>
        {rangeResult && (
          <div style={{ marginTop: 12, fontSize: 13 }}>
            <div>总存证数：{rangeResult.total}</div>
            <div>断链数：{rangeResult.broken}</div>
            <div>
              连续性：{rangeResult.continuous ? <span style={{ color: '#166534' }}>✓ 链完整连续</span> : <span style={{ color: '#dc2626' }}>✗ 存在断链</span>}
            </div>
          </div>
        )}
      </Card>

      <Card title={`存证链（最近 ${list.data?.length ?? 0} 条）`}>
        <Table<Evidence>
          rows={list.data ?? []}
          empty="暂无存证"
          columns={[
            { key: 'flow_type', title: '流类型', render: (r) => flowLabel(r.flow_type) },
            { key: 'entity_type', title: '实体' },
            { key: 'entity_id', title: '实体ID' },
            { key: 'chain_hash', title: 'chain_hash', render: (r) => <code>{r.chain_hash.slice(0, 16)}...</code> },
            { key: 'prev', title: 'prev', render: (r) => <code>{r.prev_chain_hash.slice(0, 16)}...</code> },
            { key: 'timestamp', title: '时间', render: (r) => time(r.timestamp) },
          ]}
        />
        <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
          每条 chain_hash = SHA256(payload_hash ‖ 上一条chain_hash ‖ 时间戳)。任何篡改都会导致后续全部校验失败。
        </div>
      </Card>
    </>
  )
}

function flowLabel(f: string): string {
  const m: Record<string, string> = {
    CONTRACT_FLOW: '合同流',
    FUND_FLOW: '资金流',
    INVOICE_FLOW: '发票流',
    LOGISTICS_FLOW: '物流流',
  }
  return m[f] ?? f
}
